/**
 * What a hypothesis rests on (PRD-0075 R-E3/R-E4, ADR-0095): the research runs started from it
 * (*Spustiť výskum*) and the investigations that are evidence for it (*Pridať dôkaz*), with their
 * assessment notes. Every write refreshes the hypothesis and all it rests on.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { analyticHypothesesApi } from '@/services/api/analytic-hypotheses'
import type { HypothesisResearchRun } from '@/types/analytic-hypothesis'
import { analyticHypothesisKeys, asOfKey, retryUnlessRefused, settle } from './queries'

/** How often runs are read again while one of them is still queued or running. */
const RUN_POLL_MS = 5_000

/** A run the agent has not finished yet. */
export function isRunActive(run: Pick<HypothesisResearchRun, 'status'>): boolean {
  return run.status === 'queued' || run.status === 'running'
}

export function useHypothesisResearchRuns(caseId: string, hypothesisId: string, asOf?: string) {
  return useQuery({
    queryKey: asOfKey(analyticHypothesisKeys.researchRuns(caseId, hypothesisId), asOf),
    queryFn: ({ signal }) =>
      analyticHypothesesApi.researchRuns(caseId, hypothesisId, { asOf, signal }),
    enabled: Boolean(caseId && hypothesisId),
    retry: retryUnlessRefused,
    refetchInterval: query => (query.state.data?.runs.some(isRunActive) ? RUN_POLL_MS : false),
  })
}

export function useHypothesisInvestigations(caseId: string, hypothesisId: string, asOf?: string) {
  return useQuery({
    queryKey: asOfKey(analyticHypothesisKeys.investigations(caseId, hypothesisId), asOf),
    queryFn: ({ signal }) =>
      analyticHypothesesApi.investigations(caseId, hypothesisId, { asOf, signal }),
    enabled: Boolean(caseId && hypothesisId),
    retry: retryUnlessRefused,
  })
}

/** Starting research and linking evidence for one hypothesis. */
export function useHypothesisEvidenceWrites(caseId: string, hypothesisId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: analyticHypothesisKeys.one(caseId, hypothesisId) })

  const start = useMutation({
    mutationFn: () => analyticHypothesesApi.startResearch(caseId, hypothesisId),
    onSuccess: refresh,
  })
  const link = useMutation({
    mutationFn: (investigationId: string) =>
      analyticHypothesesApi.linkInvestigation(caseId, hypothesisId, investigationId),
    onSuccess: refresh,
  })
  const unlink = useMutation({
    mutationFn: (investigationId: string) =>
      analyticHypothesesApi.unlinkInvestigation(caseId, hypothesisId, investigationId),
    onSuccess: refresh,
  })

  return {
    startResearch: () =>
      settle(() => start.mutateAsync(), t, 'analyticHypotheses.research.startFailed'),
    starting: start.isPending,
    link: (investigationId: string) =>
      settle(() => link.mutateAsync(investigationId), t, 'analyticHypotheses.evidence.linkFailed'),
    unlink: (investigationId: string) =>
      settle(
        () => unlink.mutateAsync(investigationId),
        t,
        'analyticHypotheses.evidence.unlinkFailed'
      ),
    linking: link.isPending || unlink.isPending,
  }
}
